import { Task, TaskStatus, TaskPriority } from '@/types';

/**
 * All possible columns that can be displayed in the task table
 */
export enum TaskTableColumn {
  SELECT = 'select',
  CHECKBOX = 'checkbox',
  TASK = 'task',
  CATEGORY = 'category',
  ISSUE_TYPE = 'issueType',
  PRIORITY = 'priority',
  STATUS = 'status',
  LINKS = 'links',
  CREATED_AT = 'createdAt',
  START_DATE = 'startDate',
  DUE_DATE = 'dueDate',
  COMPLETED_AT = 'completedAt',
  RACI = 'raci',
  ASSIGNED = 'assigned',
  ACTIONS = 'actions',
}

/**
 * Context type for task table - determines behavior and features
 */
export type TaskContextType = 'myTasks' | 'projectTasks';

/**
 * Configuration for a single column
 */
export interface ColumnDefinition {
  id: string;
  label: string;
  width?: number | 'auto';
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  mobileVisible?: boolean;
}

/**
 * Table configuration defining which columns to display and enabled features
 */
export interface TaskTableConfig {
  columns: TaskTableColumn[];
  enableCompletion: boolean;
  enableDeletion: boolean;
  enableTimer: boolean;
  mobileEnabled: boolean;
  showProjectName?: boolean;
  /** When true, shows multi-select checkboxes for bulk actions */
  enableSelection?: boolean;
}

/**
 * Callbacks for task table interactions
 */
export interface TaskTableCallbacks {
  onTaskClick: (taskId: string) => void;
  onTaskComplete?: (task: Task) => Promise<void>;
  onTaskDelete?: (task: Task) => Promise<void>;
  onStartTimer?: (taskId: string, taskTitle: string) => void;
  onCheckboxChange?: (task: Task) => void;
  onMenuAction?: (action: string, task: Task, event?: React.MouseEvent<HTMLElement>) => void;
  onSelectionChange?: (taskId: string, selected: boolean) => void;
  onSelectAllChange?: (selected: boolean) => void;
}

/**
 * Permission checks for task actions
 */
export interface TaskTablePermissions {
  canDoWork: boolean;
  canUseTimer: boolean;
  canComplete: boolean;
  canDelete: boolean;
  canEdit: boolean;
  canToggleCheckbox: boolean;
}

/**
 * RACI role information for display
 */
export interface RaciChipInfo {
  label: string;
  color: string;
  bgColor: string;
  allRoles: string[];
  hasMultipleRoles: boolean;
}

/**
 * Task link/dependency counts
 */
export interface TaskLinkCounts {
  subtasks: number;
  blockedBy: number;
  blocking: number;
  hasAnyLinks: boolean;
}

/**
 * Sort configuration
 */
export interface TaskTableSort {
  column: TaskTableColumn;
  direction: 'asc' | 'desc';
}

/**
 * Filter state
 */
export interface TaskTableFilters {
  search?: string;
  status?: TaskStatus | 'all';
  priority?: TaskPriority | 'all';
}

/**
 * Statistics for task summary
 */
export interface TaskTableStats {
  total: number;
  completed: number;
  overdue: number;
  subtasks: number;
  blocked: number;
}
